/**
 * PD-16416 — bounded veterinary licenses never count as a qualifying license.
 *
 * On 2026-09-23 (PD-15726) the limited, restricted, student and pre-exam
 * veterinary credentials that do NOT grant independent practice were decided
 * out of the veterinary category: an Arkansas temporary permit (issued pending
 * the licensing exam), an Arkansas restricted license (supervised practice
 * until exam results), an Alaska student permit, and Michigan's limited
 * licenses. The matchmaker never suggests them, but a license can still come
 * to point at one through a hand link or a reviewer's confirmation. Decision
 * (Chris, 2026-09-27, option A): however one gets linked, it never satisfies
 * compliance coverage or the order check. There is no reference-only display;
 * the record stays on the Licenses tab like any other, it just never counts.
 *
 * SOURCE OF TRUTH. The register is BOUNDED_VETERINARY_PRACTICE_LICENSES in
 * scripts/license-management/lib/veterinary-scope-policy.mjs; this list is its
 * `grantsIndependentPractice: false` entries, in the shape the crm-api reads.
 * The deployed apps cannot import that script, so the list is mirrored here
 * and bounded-veterinary-license.test.ts imports the register and fails when
 * the two disagree, in either the JS or the SQL shape. A new bounded license
 * is decided in the register first; the test then says to add it here.
 *
 * RECOGNITION. By (issuing state, exact board label), the same key the
 * register and its SQL twin `veterinaryScopeLabelSql` use. The category cannot
 * be used: the Arkansas rows carry no category at all (PD-15578 expressed "out
 * of scope" as no rule), and 'OTHER' on the Alaska and Michigan rows is shared
 * with unrelated credentials. The label is per state because the same words
 * carry a different statute elsewhere: NH's 'Veterinarian Temporary Permit' is
 * a full temporary license and stays qualifying.
 */
export interface BoundedVeterinaryLicenseModel {
	/** `lm_registry.state_code`, the issuing jurisdiction. */
	stateCode: string;
	/** `lm_registry.license_type`, exactly as the board publishes it. */
	rawLicenseType: string;
}

export const NON_QUALIFYING_BOUNDED_VETERINARY_LICENSES: ReadonlyArray<BoundedVeterinaryLicenseModel> =
	Object.freeze(
		[
			{ stateCode: 'AR', rawLicenseType: 'Veterinary Temporary Permit' },
			{ stateCode: 'AR', rawLicenseType: 'Veterinary Restricted License' },
			{ stateCode: 'AK', rawLicenseType: 'VETERINARIAN_STUDENT_PERMIT' },
			{ stateCode: 'MI', rawLicenseType: 'VETERINARIAN_CLN_AC_LTD' },
			{ stateCode: 'MI', rawLicenseType: 'VETERINARIAN_ED_LTD' },
			{ stateCode: 'MI', rawLicenseType: 'VETERINARIAN_NONAPRV_PRGM_LTD' },
			{ stateCode: 'MI', rawLicenseType: 'VETERINARIAN_SENIOR_STUD_LTD' },
		].map((entry) => Object.freeze(entry)),
	);

/**
 * True when a registry row is a bounded veterinary license that can never
 * qualify. Missing values never exclude: an unlinked or orphaned license is
 * judged by the rest of the rules, not guessed out here.
 */
export function isNonQualifyingBoundedVeterinaryLicense(
	stateCode: string | null | undefined,
	licenseType: string | null | undefined,
): boolean {
	if (stateCode == null || licenseType == null) {
		return false;
	}

	const state = stateCode.trim().toUpperCase();

	return NON_QUALIFYING_BOUNDED_VETERINARY_LICENSES.some(
		(entry) =>
			entry.stateCode === state && entry.rawLicenseType === licenseType,
	);
}

const sqlString = (value: string): string => `'${value.replace(/'/g, "''")}'`;

/**
 * The SQL twin of `isNonQualifyingBoundedVeterinaryLicense`, as a boolean
 * expression over `workd_mission_control.lm_registry <alias>`. NULL-safe, so
 * it is always TRUE or FALSE: on a LEFT JOIN with no registry row it reads
 * FALSE, and its negation never drops an unlinked license through
 * three-valued logic.
 */
export function nonQualifyingBoundedVeterinaryLicenseSql(
	alias: string,
): string {
	const pairs = NON_QUALIFYING_BOUNDED_VETERINARY_LICENSES.map(
		(entry) =>
			`(COALESCE(${alias}.state_code, '') = ${sqlString(entry.stateCode)} AND COALESCE(${alias}.license_type, '') = ${sqlString(entry.rawLicenseType)})`,
	);

	return `(${pairs.join(' OR ')})`;
}

/**
 * The guard every "is this license verified / qualifying" SQL test appends
 * for its registry alias: the row is not a bounded veterinary license.
 */
export function registryLicenseQualifiesSql(alias: string): string {
	return `NOT ${nonQualifyingBoundedVeterinaryLicenseSql(alias)}`;
}
